import React, { useState, useEffect } from 'react';
import { Steps, Form, Input, Select, Button, Card, Table, Space, message, Spin } from 'antd';
import api from '../../services/api';

interface CampaignBuilderProps {
  onCampaignCreated?: () => void;
}

interface Template {
  id: number;
  name: string;
  template_type: string;
}

interface AudiencePreview {
  count: number;
  audience: Array<{
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  }>;
}

export const CampaignBuilder: React.FC<CampaignBuilderProps> = ({ onCampaignCreated }) => {
  const [form] = Form.useForm();
  const [current, setCurrent] = useState(0);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [audience, setAudience] = useState<AudiencePreview | null>(null);
  const [loading, setLoading] = useState(false);

  const campaignTypes = [
    { label: 'Birthday', value: 'birthday' },
    { label: 'Christmas', value: 'christmas' },
    { label: 'Thank You', value: 'thank_you' },
    { label: 'Maintenance Reminder', value: 'maintenance_reminder' },
    { label: 'Custom Blast', value: 'custom_blast' },
    { label: 'Reengagement', value: 'reengagement' },
  ];

  // Load templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const response = await api.get('/marketing/templates');
        setTemplates(response.data || []);
      } catch (error) {
        console.error('Error loading templates:', error);
        message.error('Failed to load templates');
      }
    };
    loadTemplates();
  }, []);

  // Get available templates for selected type
  const getTemplatesForType = (type: string) => {
    return templates
      .filter((t) => t.template_type === type || t.template_type === 'custom')
      .map((t) => ({
        label: t.name,
        value: t.id,
      }));
  };

  // Preview audience
  const handlePreviewAudience = async () => {
    const campaignType = form.getFieldValue('campaignType');
    setLoading(true);
    try {
      const filters: Record<string, any> = {};

      if (campaignType === 'birthday') {
        filters.hasBirthday = true;
      } else if (campaignType === 'maintenance_reminder') {
        filters.lastVisitDaysMin = 90;
      } else if (campaignType === 'reengagement') {
        filters.lastVisitDaysMin = 180;
      }

      const response = await api.get('/marketing/audience-preview', { params: filters });
      setAudience(response.data);
    } catch (error) {
      console.error('Error previewing audience:', error);
      message.error('Failed to preview audience');
    } finally {
      setLoading(false);
    }
  };

  // Required fields owned by each wizard step, used to scope per-step validation.
  const stepFields: Array<Array<string>> = [
    ['campaignType'], // Step 0: Campaign Type
    ['templateId'], // Step 1: Template Selection
    [], // Step 2: Target Audience (no required fields)
    ['campaignName'], // Step 3: Campaign Details
  ];

  // Validate the current step before advancing; block + surface errors in-viewport.
  const handleNext = async () => {
    const fields = stepFields[current] || [];
    if (fields.length > 0) {
      try {
        await form.validateFields(fields);
      } catch (err) {
        message.error('Please complete the required fields before continuing');
        return;
      }
    }
    setCurrent(current + 1);
  };

  // Handle campaign submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const filters: Record<string, any> = {};
      if (values.campaignType === 'birthday') {
        filters.hasBirthday = true;
      } else if (values.campaignType === 'maintenance_reminder') {
        filters.lastVisitDaysMin = 90;
      } else if (values.campaignType === 'reengagement') {
        filters.lastVisitDaysMin = 180;
      }

      await api.post('/marketing/campaigns', {
        name: values.campaignName,
        campaignType: values.campaignType,
        templateId: values.templateId,
        targetFilters: Object.keys(filters).length > 0 ? filters : null,
        channel: values.channel || 'mail',
      });

      message.success('Campaign created successfully');
      form.resetFields();
      setCurrent(0);
      onCampaignCreated?.();
    } catch (error) {
      console.error('Error creating campaign:', error);
      message.error('Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: 'Campaign Type',
      content: (
        <Form form={form} layout="vertical">
          <Form.Item
            label="Campaign Type"
            name="campaignType"
            rules={[{ required: true, message: 'Please select a campaign type' }]}
          >
            <Select placeholder="Select campaign type" options={campaignTypes} />
          </Form.Item>
        </Form>
      ),
    },
    {
      title: 'Template Selection',
      content: (
        <Form form={form} layout="vertical">
          <Form.Item
            label="Mail Template"
            name="templateId"
            rules={[{ required: true, message: 'Please select a template' }]}
          >
            <Select
              placeholder="Select template"
              options={getTemplatesForType(form.getFieldValue('campaignType'))}
            />
          </Form.Item>
        </Form>
      ),
    },
    {
      title: 'Target Audience',
      content: (
        <div>
          <Form form={form} layout="vertical">
            <Form.Item>
              <Button type="primary" onClick={handlePreviewAudience} loading={loading}>
                Preview Audience
              </Button>
            </Form.Item>
          </Form>
          {audience && (
            <Card title={`Target Audience: ${audience.count} customers`} style={{ marginTop: 16 }}>
              {audience.audience.length > 0 ? (
                <Table
                  columns={[
                    { title: 'Name', dataIndex: 'first_name', key: 'first_name', width: 150 },
                    { title: 'Email', dataIndex: 'email', key: 'email' },
                  ]}
                  dataSource={audience.audience}
                  rowKey="id"
                  pagination={{ pageSize: 5 }}
                  size="small"
                />
              ) : (
                <p>No customers match the selected criteria</p>
              )}
            </Card>
          )}
        </div>
      ),
    },
    {
      title: 'Campaign Details',
      content: (
        <Form form={form} layout="vertical">
          <Form.Item
            label="Campaign Name"
            name="campaignName"
            rules={[{ required: true, message: 'Please enter a campaign name' }]}
          >
            <Input placeholder="Enter campaign name" />
          </Form.Item>
          <Form.Item label="Channel" name="channel">
            <Select
              placeholder="Select channel"
              options={[
                { label: 'Mail', value: 'mail' },
                { label: 'Email', value: 'email' },
                { label: 'SMS', value: 'sms' },
                { label: 'Multi-channel', value: 'multi' },
              ]}
            />
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <Steps current={current} items={steps} style={{ marginBottom: 24 }} />

      <Card style={{ minHeight: 300 }}>
        {steps[current].content}
      </Card>

      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <Space>
          {current > 0 && (
            <Button onClick={() => setCurrent(current - 1)}>
              Previous
            </Button>
          )}
          {current < steps.length - 1 ? (
            <Button type="primary" onClick={handleNext}>
              Next
            </Button>
          ) : (
            <Button type="primary" onClick={handleSubmit} loading={loading}>
              Create Campaign
            </Button>
          )}
        </Space>
      </div>
    </Spin>
  );
};

export default CampaignBuilder;
